import { ErrorView } from "@/components/ui/layout/error/errorView"
import { SessionBlockEntityFactory } from "@/modules/sessions/entities/sessionBlockEntity"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import type { SidebarNavigatorParams } from "@/modules/sidebar/sidebarTypes"
import { TemplateForm, type TemplateFormRef } from "@/modules/template/components/sidebarForm/templateForm"
import { TemplateSaveWeekForm } from "@/modules/template/components/sidebarForm/templateSaveWeekForm"
import {
  TemplateSessionEditForm,
  TemplateSessionFormWrapper,
} from "@/modules/template/components/sidebarForm/templateSessionForm"
import { TemplateImportExerciseView } from "@/modules/template/components/sidebarImport/templateImportExerciseView"
import { TemplateImportSessionView } from "@/modules/template/components/sidebarImport/templateImportSessionView"
import { TemplateImportWeekView } from "@/modules/template/components/sidebarImport/templateImportWeekView"
import { TemplateCategoriesView } from "@/modules/template/components/sidebarView/templateCategoriesView"
import { TemplateSessionView } from "@/modules/template/components/sidebarView/templateSessionView"
import { TemplateSessionViewWrapper } from "@/modules/template/components/sidebarView/templateSessionViewWrapper"
import type { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import type { EditExerciseTemplateInputDto } from "@/modules/template/gateway/templateDto"
import { useTemplateElement } from "@/modules/template/hooks/useTemplate"
import { useTemplateCategriesActions } from "@/modules/template/stores/useTemplateCategriesStore"
import { useTemplateImportActions } from "@/modules/template/stores/useTemplateImportStore"
import { TemplateType } from "@/modules/template/types/templateType"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { Suspense, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"

export const useTemplateCategoriesSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const { setSearchTerms } = useTemplateCategriesActions()

  return {
    navigateToTemplateCategories: (args?: SidebarNavigatorParams) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateCategoriesView />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          setSearchTerms("")
          return Promise.resolve(true)
        },
        ...args,
      })
    },
  }
}

export const useTemplateExerciseImportSidebar = () => {
  const { addSidebarStackOnTop, hideSidebarStackOnTop } = useSidebar()
  const { clearSelectedTemplate } = useTemplateImportActions()

  return {
    navigateToTemplateExerciseImportView: (
      orderIndex: number,
      onValidate: (exercise: ExerciseFormikType) => Promise<void>,
      args?: SidebarNavigatorParams,
    ) => {
      document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })
      document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
      document.querySelector('[data-slot="right-sidebar-content-stack-on-top"]')?.scrollTo({ top: 0 })

      addSidebarStackOnTop({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateImportExerciseView
                orderIndex={orderIndex}
                onValidate={onValidate}
                onCancel={() => {
                  clearSelectedTemplate()
                  hideSidebarStackOnTop()
                }}
              />
            </Suspense>
          </ErrorBoundary>
        ),
        sidebarLeftButtonClick() {
          clearSelectedTemplate()
          hideSidebarStackOnTop()
          return Promise.resolve(true)
        },
        ...args,
      })
    },
  }
}

export const useTemplateSessionImportSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const { clearSelectedTemplate } = useTemplateImportActions()

  return {
    navigateToTemplateSessionImport: (
      programId: string,
      options: { date: Date | null; index: number | null; virtualProgramByDateIndex: number | null },
      args?: SidebarNavigatorParams,
    ) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateImportSessionView programId={programId} options={options} />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          clearSelectedTemplate()
          return Promise.resolve(true)
        },
        ...args,
      })
    },
  }
}

export const useTemplateWeekImportSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const { clearSelectedTemplate } = useTemplateImportActions()
  return {
    navigateToTemplateWeekImport: (
      programId: string,
      options: { startDate: Date | null; startIndex: number | null },
      args?: SidebarNavigatorParams,
    ) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateImportWeekView programId={programId} options={options} />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          clearSelectedTemplate()
          return Promise.resolve(true)
        },
        ...args,
      })
    },
  }
}

export const useTemplateSaveWeekFormSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  return {
    navigateToTemplateSaveWeekForm: ({ sessionIds }: { sessionIds: string[] }, args?: SidebarNavigatorParams) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateSaveWeekForm sessionIds={sessionIds} />
            </Suspense>
          </ErrorBoundary>
        ),
        ...args,
      })
    },
  }
}

export const useTemplateFormSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const templateFormRef = useRef<TemplateFormRef>(null)
  return {
    navigateToTemplateForm: (
      { templateId, onHide }: { templateId: string; onHide?: () => void },
      args?: SidebarNavigatorParams,
    ) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateForm templateId={templateId} ref={templateFormRef} />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          if (onHide) {
            onHide()
          }
          return templateFormRef.current ? templateFormRef.current.onClose() : Promise.resolve(true)
        },
        ...args,
      })
    },
  }
}

export const useTemplateSessionWrapperFormSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  return {
    navigateToTemplateSessionFormWrapper: ({ templateId }: { templateId: string }, args?: SidebarNavigatorParams) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateSessionFormWrapper templateId={templateId} />
            </Suspense>
          </ErrorBoundary>
        ),
        ...args,
      })
    },
  }
}

export const useTemplateSessionEditFormSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  return {
    navigateToTemplateSessionEditForm: (
      {
        templateId,
        session,
        sessionIndex,
      }: { templateId: string; session?: TemplateSessionEntity; sessionIndex?: number },
      args?: SidebarNavigatorParams,
    ) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateSessionEditForm templateId={templateId} session={session} sessionIndex={sessionIndex} />
            </Suspense>
          </ErrorBoundary>
        ),
        ...args,
      })
    },
  }
}

export const useTemplateSessionViewWithTemplateSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  return {
    navigateToTemplateSessionViewWithTemplate: (
      { templateId, session }: { templateId: string; session?: TemplateSessionEntity },
      args?: SidebarNavigatorParams,
    ) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateSessionViewWrapper templateId={templateId} session={session} />
            </Suspense>
          </ErrorBoundary>
        ),
        ...args,
      })
    },
  }
}

export const useTemplateSessionViewWithSessionSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  return {
    navigateToTemplateSessionViewWithSession: (
      { session }: { session: TemplateSessionEntity },
      args?: SidebarNavigatorParams,
    ) => {
      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateSessionView session={session} />
            </Suspense>
          </ErrorBoundary>
        ),
        ...args,
      })
    },
  }
}

export const useTemplateSingleBlockFormEditionSidebar = (templateId: string) => {
  const { navigateToExerciseForm } = useSessionSidebar()
  const { data: templateElement } = useTemplateElement(templateId)

  return {
    navigateToTemplateSingleBlockFormEdition: (
      onValidated: (updatedBlock: EditExerciseTemplateInputDto) => Promise<void>,
    ) => {
      const block = "exercise" in templateElement ? templateElement.exercise : null
      if (!block || templateElement.type !== TemplateType.EXERCISE) throw new Error("Block not found")
      const blockFormik = SessionBlockEntityFactory.fromEntityToFormik({
        type: SessionBlockType.SESSION_EXERCISE,
        content: block,
      })
      if (blockFormik.type === SessionBlockType.SESSION_EXERCISE) {
        navigateToExerciseForm(
          blockFormik.content,
          0,
          async (exercise: ExerciseFormikType) => {
            const newBlock = SessionBlockExerciseEntity.fromFormikToTemplateInputDto(exercise)
            await onValidated(newBlock)
          },
          { replaceAllComponents: false },
        )
      } else {
        throw new Error("Block type not supported")
      }
    },
  }
}
